import { useDropZone } from "@vueuse/core";
import { computed, toValue } from "vue";
import { useNetCoords } from "./netCoordsModifier";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";

import type { CanvasDimensions } from "./CanvasDimensions";
import type { MaybeRefOrGetter, Ref } from "vue";
import { fileToDataURL, getDataUrlDimensions, warnIfFileIsLarge } from "@/helpers/utility/File";
import { useSceneMediumStore } from "@editor/items/sceneMedium/sceneMedia.store";
import { useSceneTextStore } from "@editor/items/sceneText/sceneTexts.store";

export function useNetDropHandler(canvasElement: Ref<HTMLElement | undefined>, mode: MaybeRefOrGetter<"edit" | string>, canvasDimensions: Ref<CanvasDimensions>) {
  const sceneMediaStore = useSceneMediumStore();
  const sceneTextStore = useSceneTextStore();

  const toast = useToast();
  const { t } = useI18n();

  const { toLocal } = useNetCoords({
    canvasDimensions: canvasDimensions
  });
  
  const { isOverDropZone } = useDropZone(canvasElement, {
    onDrop: async (files: File[] | null, event) => {
      if (!files) return;
      if (toValue(mode) !== "edit") return;

      const { clientX, clientY } = event;
      const { x: localX, y: localY } = toLocal({x: clientX, y: clientY});

      for (const file of files) {
        const type = file.type;
        console.log("import file:", file.name);

        if (type.startsWith("image/")) {
          warnIfFileIsLarge(file, 4); // Warn if the file is larger than 4MB

          const dataUrl = await fileToDataURL(file);
          const dimensions = await getDataUrlDimensions(dataUrl, type);
          
          sceneMediaStore.create({
            x: localX,
            y: localY,
            width: dimensions.width,
            height: dimensions.height,
          }, {
            mediaType: type,
            data: dataUrl,
          });
        }

        else if (type.startsWith("video/")) {
          warnIfFileIsLarge(file, 5); // Warn if the file is larger than 4MB

          const dataUrl = await fileToDataURL(file);
          const dimensions = await getDataUrlDimensions(dataUrl, type);
          
          sceneMediaStore.create({
            x: localX,
            y: localY,
            width: dimensions.width,
            height: dimensions.height,
          }, {
            mediaType: type,
            data: dataUrl,
          });
        }

        else if (file.type == "text/plain") {
          file.text().then((text) => {
            sceneTextStore.create({
              x: localX,
              y: localY,
              width: 300,
              height: 200,
            }, {
              text: text.replace(/\n/g, "<br>"),
            });
          });
        }

        // PDFs are buggy - should they be interactive? 
        // This collides with the canvas interactions.
        // So: disable PDFs for now.
        
        // else if (file.type == "application/pdf") {
        //   netMediaStore.create({
        //     data: file,
        //     mediaType: type,
        //     x: localX,
        //     y: localY,
        //     width: 300,
        //     height: 200,
        //     aspectRatioFixed: false
        //   });
        // }

        else {
          toast.error(t("errors.fileTypeNotDroppable", { type }));
        }
      }
    }
  });

  const isOverDropzone = computed(() => {
    if (toValue(mode) !== "edit") return false;
    return isOverDropZone.value;
  });

  return {
    isOverDropzone
  };
}
